// src/pages/Cookies.jsx
import React from "react";
import LegalPageLayout from "./LegalPageLayout";
import LegalDocumentContent from "./LegalDocumentContent";

export default function CookiesPage() {
  const cookiesText = `GWIN COOKIE POLICY
Effective date: 1 May 2026
Platform: Gwin
Operator: Marion Rich Pty Ltd, registration number 2022 / 577834 / 07, trading as “Gwin”
Jurisdiction: Republic of South Africa
Contact email: admin@gwin.co.za

1. Introduction
This Cookie Policy explains how Gwin uses cookies and similar technologies when you visit or use our website, web application, mobile application, learning platform, dashboards, assessments, templates, subscription pages, and related digital services.
Cookies help Gwin provide a secure, reliable, personalised, and improved user experience.
This Cookie Policy should be read together with our:
Terms of Use;
Privacy Policy;
POPIA Consent Notice.
By using Gwin, you agree that we may use cookies and similar technologies as described in this Cookie Policy, subject to your cookie choices and applicable law.

2. What Are Cookies?
Cookies are small text files placed on your device when you visit a website or use an online service.
Cookies allow a website or platform to recognise your device, remember your preferences, keep you logged in, secure your account, understand how the platform is used, and improve your experience.
Cookies may be:
Session cookies
These are temporary cookies that are deleted when you close your browser.
Persistent cookies
These remain on your device for a set period or until you delete them.
First-party cookies
These are placed by Gwin directly.
Third-party cookies
These are placed by third-party services we use, such as analytics, payment, communication, advertising, or platform tools.

3. Similar Technologies We May Use
In addition to cookies, Gwin may use similar technologies such as:
pixels;
tags;
beacons;
SDKs;
local storage;
session storage;
device identifiers;
analytics scripts;
tracking links;
log files.
In this Cookie Policy, when we refer to “cookies”, we also include these similar technologies where relevant.

4. Why Gwin Uses Cookies
Gwin may use cookies to:
keep you logged in;
remember your preferences;
secure your account;
prevent fraud and abuse;
detect account sharing;
protect against scraping or automated access;
provide lessons, dashboards, and app features;
process subscriptions and payments;
measure platform performance;
understand which lessons and features users engage with;
improve onboarding;
reduce drop-off;
personalise your learning journey;
analyse marketing performance;
remember cookie choices;
improve platform reliability.

5. Types of Cookies We Use

5.1 Strictly Necessary Cookies
These cookies are required for Gwin to work properly.
They may be used for:
account login;
authentication;
session management;
security;
fraud prevention;
payment checkout;
consent management;
load balancing;
saving privacy preferences;
platform stability.
Without these cookies, some parts of Gwin may not function.
Because these cookies are necessary to provide the service, they cannot usually be disabled through Gwin’s cookie settings. You may block them in your browser, but this may cause the Platform to stop working properly.
Examples:
keeping you signed in;
remembering that you accepted or rejected optional cookies;
protecting your account against suspicious access;
allowing secure payment checkout.

5.2 Functional Cookies
Functional cookies help Gwin remember your choices and improve convenience.
They may be used to remember:
language preferences;
display settings;
saved filters;
learning preferences;
last viewed lesson;
progress display preferences;
device preferences;
region or province selection.
If you disable functional cookies, some personalisation features may not work properly.

5.3 Performance and Analytics Cookies
Performance and analytics cookies help us understand how users interact with Gwin.
They may tell us:
which pages are visited;
which lessons are started or completed;
where users drop off;
how long users spend on certain sections;
which features are most useful;
whether users experience errors;
whether onboarding is working;
which devices and browsers are used;
how fast the Platform loads.
We use this information to improve the Platform, improve learning flows, fix issues, reduce friction, and build better user experiences.
Where possible, we use aggregated or anonymised information for analytics.

5.4 Security and Fraud Prevention Cookies
Security cookies help us protect Gwin, users, and platform integrity.
They may be used to:
detect suspicious logins;
prevent unauthorised access;
detect account sharing;
detect automated scraping;
prevent abuse of trials or subscriptions;
protect payment flows;
monitor unusual activity;
prevent score manipulation;
protect intellectual property and platform content.
These cookies support the security and integrity of the Platform.

5.5 Personalisation Cookies
Personalisation cookies help Gwin tailor your experience.
They may be used to:
recommend next lessons;
remember your learning journey;
show relevant prompts;
personalise dashboards;
show relevant templates;
adjust content to your business stage or industry;
show progress reminders;
display relevant readiness prompts.
Personalisation helps Gwin provide a more useful and relevant experience.

5.6 Marketing and Advertising Cookies
Marketing and advertising cookies may be used to:
measure campaign performance;
understand which marketing channels led users to Gwin;
show relevant Gwin content or offers;
avoid showing the same message repeatedly;
measure conversions;
support retargeting campaigns;
understand audience interest.
These cookies may be placed by Gwin or third-party advertising and analytics providers.
Marketing cookies are optional and should only be used where lawful and where required consent has been obtained.

5.7 Partner and Programme Cookies
If you access Gwin through a partner, sponsor, advisor, incubator, accelerator, ESD programme, or institutional programme, cookies may help us:
identify your programme cohort;
apply sponsored access;
track programme participation;
support partner dashboards;
measure completion;
provide programme reporting;
prevent unauthorised programme access.
Where personal information is shared with a partner, this will be handled according to the Privacy Policy, POPIA Consent Notice, and any applicable programme terms.

6. Third-Party Cookies
Gwin may use third-party tools that place cookies or similar technologies.
These may include providers for:
hosting;
analytics;
error monitoring;
payment processing;
email communication;
WhatsApp or SMS communication;
customer support;
authentication;
marketing;
advertising;
product analytics;
consent management;
fraud prevention.
Examples of third-party tools that may be used include:
Google Analytics;
Meta Pixel;
Google Ads;
payment gateways;
customer support chat tools;
email platforms;
analytics platforms;
hosting and infrastructure tools.
The exact providers used by Gwin may change over time.
Third-party providers may process information according to their own privacy and cookie policies. Gwin is not responsible for third-party policies, but we aim to use reputable providers and appropriate contractual safeguards where required.

7. Information Collected Through Cookies
Cookies may collect or help process information such as:
IP address;
device type;
browser type;
operating system;
approximate location;
session ID;
user ID or account ID;
pages visited;
buttons clicked;
features used;
lessons accessed;
time spent on pages;
referral source;
marketing campaign source;
error reports;
cookie preferences;
login status;
subscription status;
security signals.
Where this information identifies you or can reasonably be linked to you, we treat it as personal information and process it in accordance with our Privacy Policy and POPIA.

8. How Long Cookies Stay on Your Device
Cookies may remain on your device for different periods.
Session cookies are deleted when you close your browser.
Persistent cookies may remain for a set period, depending on their purpose.
Typical retention examples:
authentication cookies: for the duration of your session or a short security period;
preference cookies: until changed or deleted;
analytics cookies: for a defined analytics period;
marketing cookies: for a limited campaign measurement period;
consent cookies: long enough to remember your cookie choices.
You can delete cookies through your browser settings at any time.

9. Your Cookie Choices
You can manage cookies in several ways.

9.1 Cookie Banner or Consent Tool
Where available, Gwin may provide a cookie banner or cookie settings tool.
This may allow you to:
accept all cookies;
reject optional cookies;
manage cookie categories;
change cookie choices later.
Strictly necessary cookies may remain active because they are required for the Platform to function.

9.2 Browser Settings
You can control cookies through your browser settings.
Most browsers allow you to:
block cookies;
delete cookies;
block third-party cookies;
receive alerts before cookies are placed;
clear browsing data.
If you block or delete cookies, some Gwin features may not work properly.

9.3 Mobile Device Settings
If you use a mobile device, your operating system may allow you to limit tracking, reset advertising identifiers, or manage app permissions.

10. What Happens If You Disable Cookies?
If you disable cookies, you may still be able to use parts of Gwin, but some features may not work correctly.
You may experience issues with:
logging in;
staying logged in;
payment checkout;
lesson progress saving;
dashboard display;
security verification;
cookie preference saving;
personalisation;
analytics-based improvements;
partner or sponsored access;
support features.
Disabling strictly necessary cookies may make the Platform unusable.

11. Cookies and POPIA
Where cookies collect personal information, Gwin processes that information in accordance with POPIA and our Privacy Policy.
This means we aim to:
process information lawfully and transparently;
collect only what is reasonably necessary;
use information for specific purposes;
protect information with reasonable security safeguards;
respect your rights under POPIA;
obtain consent where required;
allow you to opt out of optional cookies where applicable.

12. Cookies and Direct Marketing
Marketing cookies may help Gwin understand marketing performance and show relevant messages.
Where direct marketing laws or privacy laws require consent, we will request consent before using information for certain marketing purposes.
You may opt out of marketing communication at any time.
Turning off marketing cookies does not automatically unsubscribe you from email or WhatsApp communication. You may need to use the unsubscribe link or update your communication preferences.

13. Cookies and Children
Gwin is not intended for children under the age of 18.
We do not knowingly use cookies to track children.
If we discover that a child has used Gwin without proper authority, we may take steps to delete or deactivate the account and related information.

14. Updates to This Cookie Policy
We may update this Cookie Policy from time to time.
We may update it to reflect:
changes in law;
changes in technology;
new cookies or tools;
changes to our Platform;
new analytics or marketing providers;
improved privacy practices.
The updated Cookie Policy will apply from the effective date shown at the top.
If the changes are material, we will take reasonable steps to notify users or request updated consent where required.

15. Contact Details
For cookie-related questions, privacy requests, or consent questions, contact:
Marion Rich Pty Ltd t/a Gwin
Registration number: 2022 / 577834 / 07
Information Officer / Privacy Contact: Candice Shunmoogam
Email: admin@gwin.co.za
Address: 22 Percy Road, Ottery, Cape Town
Website: www.gwin.co.za`;

  return (
    <LegalPageLayout title="Cookies" backTo="/contact" backLabel="Back">
      <LegalDocumentContent documentText={cookiesText} />
    </LegalPageLayout>
  );
}